import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import api from "../api/axios";

const money = (value, currency) =>
  `${Number(value || 0).toLocaleString()} ${currency || ""}`.trim();

const LoanRepaymentForm = ({ loan, onSuccess, onCancel }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const remaining = useMemo(
    () => Number(loan?.amount_remaining ?? loan?.principal_amount ?? 0),
    [loan]
  );
  const numericAmount = Number(amount);
  const isValid =
    Number.isFinite(numericAmount) &&
    numericAmount > 0 &&
    numericAmount <= remaining;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      enqueueSnackbar("Saisissez un montant de remboursement valide.", {
        variant: "warning",
      });
      return;
    }

    if (numericAmount > remaining) {
      enqueueSnackbar(
        `Le remboursement ne peut pas dépasser le solde restant de ${money(
          remaining,
          loan.currency
        )}.`,
        { variant: "warning" }
      );
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/loan-repayments", {
        loan_id: loan.id,
        amount: numericAmount,
        currency: loan.currency,
        note: note.trim() || null,
      });

      const newRemaining = Number(
        response?.data?.amount_remaining ?? remaining - numericAmount
      );

      enqueueSnackbar(
        newRemaining > 0
          ? `Remboursement partiel enregistré. Reste à recouvrer : ${money(
              newRemaining,
              loan.currency
            )}.`
          : "Prêt intégralement remboursé.",
        { variant: "success" }
      );

      setAmount("");
      setNote("");
      if (typeof onSuccess === "function") onSuccess(response.data);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Impossible d’enregistrer le remboursement.";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      variant="outlined"
      sx={{ p: 2.5, mt: 1.5, borderRadius: 2.5 }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Enregistrer un remboursement
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loan?.debtor_name || "Débiteur"} • Prêt #{loan?.id}
          </Typography>
        </Box>

        <Alert severity="info">
          Montant initial : <strong>{money(loan?.principal_amount, loan?.currency)}</strong>
          {" • "}Reste à recouvrer :{" "}
          <strong>{money(remaining, loan?.currency)}</strong>
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <TextField
              required
              fullWidth
              label="Montant remboursé"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={loading}
              inputProps={{ min: 0.01, max: remaining, step: "0.01" }}
              helperText={
                amount && numericAmount > remaining
                  ? "Le montant dépasse le solde restant."
                  : `Maximum : ${money(remaining, loan?.currency)}`
              }
              error={Boolean(amount) && (!isValid || numericAmount > remaining)}
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              label="Note / référence"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={loading}
              inputProps={{ maxLength: 500 }}
              helperText="Facultatif — ex. référence du paiement ou observation"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          {onCancel ? (
            <Button onClick={onCancel} disabled={loading}>
              Annuler
            </Button>
          ) : null}
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !isValid}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {loading ? "Enregistrement..." : "Confirmer le remboursement"}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default LoanRepaymentForm;
